import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Calendar, Maximize, User, Clock, Tag, ArrowRight, ArrowLeft } from 'lucide-react';
import { projectsApi } from '../api/projects';
import p1 from '../assets/p1.webp';
import p2 from '../assets/p2.webp';
import p3 from '../assets/p3.webp';
import './ProjectDetails.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const IMG_BASE = BASE_URL.replace('/api', '');
const FALLBACK = [p1, p2, p3];

export function ProjectDetails() {
    const { projectId } = useParams();
    const { language, t } = useLanguage();
    const isRtl = language === 'ar';
    const [project, setProject] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        const load = async () => {
            try {
                const data = await projectsApi.getById(projectId);
                setProject(data);
                const all = await projectsApi.getAll();
                setRelated(all.filter(p => p.id !== data.id).slice(0, 3));
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };
        load();
    }, [projectId]);

    if (loading) return (
        <div className="master-loader">
            <div className="master-spinner"></div>
        </div>
    );
    
    if (!project) return <div className="master-error">{isRtl ? 'المشروع غير موجود' : 'Project not found'}</div>;

    const en = !isRtl;
    const title = (en && project.titleEn) ? project.titleEn : project.title;
    const description = (en && project.descriptionEn) ? project.descriptionEn : project.description;
    const location = (en && project.locationEn) ? project.locationEn : project.location;
    const challenge = (en && project.challengeEn) ? project.challengeEn : project.challenge;
    const result = (en && project.resultEn) ? project.resultEn : project.result;
    const scopeRaw = (en && project.scopeOfWorkEn) ? project.scopeOfWorkEn : project.scopeOfWork;
    const scopeItems = scopeRaw ? scopeRaw.split('\n').filter(s => s.trim()) : [];
    
    const rawCategory = (project.category || project.finishingType || '').toLowerCase();
    const translationMap = {
        'construction': t('portfolio.filters.construction'),
        'interior': t('portfolio.filters.interior'),
        'facades': t('portfolio.filters.facades'),
        'finishing': t('portfolio.filters.finishing')
    };
    const category = translationMap[rawCategory] || project.category || project.finishingType;
    
    const cover = project.images?.find(i => i.isPrimary) || project.images?.[0];
    const gallery = project.images?.filter(i => !i.isPrimary) || [];
    const heroImg = cover?.imageUrl ? IMG_BASE + cover.imageUrl : p1;

    return (
        <main className="master-page" dir={isRtl ? 'rtl' : 'ltr'}>
            
            {/* 1. TOP HERO WITH UNCUT IMAGE */}
            <section className="master-hero">
                <div className="master-hero-blur" style={{ backgroundImage: `url(${heroImg})` }}></div>
                <div className="master-hero-overlay"></div>
                
                <div className="master-container master-hero-inner">
                    <div className="master-hero-text">
                        <div className="master-breadcrumbs">
                            <Link to="/">{t('nav.home')}</Link>
                            <span className="master-sep">/</span>
                            <Link to="/portfolio">{t('nav.projects')}</Link>
                        </div>
                        <h1 className="master-title">{title}</h1>
                    </div>
                    <div className="master-image-frame">
                        <img src={heroImg} alt={title} className="master-hero-img" />
                    </div>
                </div>
            </section>

            {/* 2. OVERLAPPING GLASS PANEL */}
            <div className="master-container master-panel-container">
                <div className="master-glass-panel">
                    {category && (
                        <div className="master-panel-item">
                            <span className="master-panel-label">{isRtl ? 'التصنيف' : 'Category'}</span>
                            <span className="master-panel-val">{category}</span>
                        </div>
                    )}
                    {location && (
                        <div className="master-panel-item">
                            <span className="master-panel-label">{isRtl ? 'الموقع' : 'Location'}</span>
                            <span className="master-panel-val">{location}</span>
                        </div>
                    )}
                    {project.area && (
                        <div className="master-panel-item">
                            <span className="master-panel-label">{isRtl ? 'المساحة' : 'Area'}</span>
                            <span className="master-panel-val">{project.area}</span>
                        </div>
                    )}
                    {project.completedAt && (
                        <div className="master-panel-item">
                            <span className="master-panel-label">{isRtl ? 'سنة التنفيذ' : 'Year'}</span>
                            <span className="master-panel-val">{new Date(project.completedAt).getFullYear()}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* 3. ALIGNED BODY GRID */}
            <section className="master-body">
                <div className="master-container">
                    <div className="master-grid-layout">
                        
                        {/* LEFT COLUMN: STORY */}
                        <div className="master-main-col">
                            <div className="master-section-head">
                                <div className="master-gold-dash"></div>
                                <h2>{isRtl ? 'رؤية المشروع' : 'The Vision'}</h2>
                            </div>
                            <p className="master-desc">{description || (isRtl ? 'لا يوجد وصف' : 'No description available')}</p>

                            {/* Scope */}
                            {scopeItems.length > 0 && (
                                <div className="master-scope-block">
                                    <h3>{isRtl ? 'نطاق العمل' : 'Scope of Work'}</h3>
                                    <div className="master-scope-grid">
                                        {scopeItems.map((item, i) => (
                                            <div key={i} className="master-scope-item">
                                                <div className="master-dot"></div>
                                                <span>{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Challenge & Result (FIXED COLORS) */}
                            {(challenge || result) && (
                                <div className="master-cr-grid">
                                    {challenge && (
                                        <div className="master-cr-card master-cr-dark">
                                            <h4>{isRtl ? 'التحدي' : 'Challenge'}</h4>
                                            <p>{challenge}</p>
                                        </div>
                                    )}
                                    {result && (
                                        <div className="master-cr-card master-cr-light">
                                            <h4>{isRtl ? 'النتيجة' : 'Result'}</h4>
                                            <p>{result}</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* COLORED GALLERY */}
                            {gallery.length > 0 && (
                                <div className="master-gallery-block">
                                    <div className="master-section-head">
                                        <div className="master-gold-dash"></div>
                                        <h2>{isRtl ? 'معرض الصور' : 'Gallery'}</h2>
                                    </div>
                                    <div className="master-gallery-grid">
                                        {gallery.map((img, i) => (
                                            <div key={i} className={`master-gallery-item ${i === 0 ? 'master-featured' : ''}`}>
                                                <img src={IMG_BASE + img.imageUrl} alt={`${title} - ${i}`} className="master-gallery-img" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* RIGHT COLUMN: STICKY SIDEBAR */}
                        <div className="master-sidebar-col">
                            <div className="master-sticky-card">
                                <h3>{isRtl ? 'تفاصيل إضافية' : 'Project Facts'}</h3>
                                
                                <div className="master-facts-list">
                                    {project.client && (
                                        <div className="master-fact-row">
                                            <User size={20} className="master-fact-icon" />
                                            <div>
                                                <span>{isRtl ? 'العميل' : 'Client'}</span>
                                                <strong>{project.client}</strong>
                                            </div>
                                        </div>
                                    )}
                                    {project.duration && (
                                        <div className="master-fact-row">
                                            <Clock size={20} className="master-fact-icon" />
                                            <div>
                                                <span>{isRtl ? 'مدة التنفيذ' : 'Duration'}</span>
                                                <strong>{project.duration}</strong>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="master-actions">
                                    <Link to="/request-quote" className="master-btn master-btn-solid">
                                        <span>{isRtl ? 'اطلب مشروع مشابه' : 'Request Similar Project'}</span>
                                        {isRtl ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                                    </Link>
                                    <Link to="/contact" className="master-btn master-btn-outline">
                                        {isRtl ? 'تواصل معنا' : 'Contact Us'}
                                    </Link>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* 4. RELATED PROJECTS (RESTORED!) */}
            {related.length > 0 && (
                <section className="master-related-section">
                    <div className="master-container">
                        
                        <div className="master-section-head" style={{ justifyContent: 'center', marginBottom: '50px' }}>
                            <div className="master-gold-dash"></div>
                            <h2>{isRtl ? 'مشاريع مشابهة' : 'Related Projects'}</h2>
                            <div className="master-gold-dash"></div>
                        </div>

                        <div className="master-related-grid">
                            {related.map((rp, i) => {
                                const rpCover = rp.images?.find(img => img.isPrimary) || rp.images?.[0];
                                const rpTitle = (!isRtl && rp.titleEn) ? rp.titleEn : rp.title;
                                const rpRawCat = (rp.category || rp.finishingType || '').toLowerCase();
                                const rpCat = translationMap[rpRawCat] || rp.category;

                                return (
                                    <Link to={`/portfolio/${rp.id}`} key={rp.id} className="master-related-card">
                                        <div className="master-rcard-img">
                                            <img src={rpCover ? IMG_BASE + rpCover.imageUrl : FALLBACK[i % 3]} alt={rpTitle} />
                                            <div className="master-rcard-tag">{rpCat}</div>
                                        </div>
                                        <div className="master-rcard-info">
                                            <h3>{rpTitle}</h3>
                                            <div className="master-rcard-link">
                                                <span>{isRtl ? 'التفاصيل' : 'Details'}</span>
                                                {isRtl ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                        
                        <div className="master-back-wrapper">
                            <Link to="/portfolio" className="master-btn-back">
                                {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
                                <span>{isRtl ? 'العودة لجميع المشاريع' : 'Back to All Projects'}</span>
                            </Link>
                        </div>

                    </div>
                </section>
            )}
            
        </main>
    );
}
